import gsap from "gsap";
import SplitType from "split-type";
import { whenIntroStart, whenSiteReady } from "./site-ready";

/**
 * Reveal ligne par ligne (style Polite Chaos / Copy.jsx) :
 * split en lignes, masque overflow, chaque ligne remonte de y 100% → 0%.
 */
const DURATION = 1;
const LINE_STAGGER = 0.1;
const EASE = "power4.out";

export default class SplitReveal {
	constructor() {
		this.revealed = new Set();
		this.initialized = false;

		whenIntroStart(() => this.prepHeader());
		whenSiteReady(() => this.init());
	}

	async prepHeader() {
		const items = document.querySelectorAll(".c-header [data-split-reveal]");
		if (!items.length) return;

		try {
			await (document.fonts?.ready ?? Promise.resolve());
		} catch (e) {}

		items.forEach((el) => this.split(el));
	}

	async init() {
		if (this.initialized) return;
		this.initialized = true;

		this.items = [...document.querySelectorAll("[data-split-reveal]")];
		if (!this.items.length) return;

		try {
			await (document.fonts?.ready ?? Promise.resolve());
		} catch (e) {}

		this.items.forEach((el) => this.split(el));

		this.observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					this.reveal(entry.target);
				});
			},
			{ rootMargin: "0px 0px -10% 0px" }
		);

		this.items.forEach((el) => {
			if (this.isInView(el)) {
				this.reveal(el);
			} else {
				this.observer.observe(el);
			}
		});
	}

	isInView(el) {
		const { top, bottom } = el.getBoundingClientRect();
		return bottom > 0 && top < window.innerHeight * 0.9;
	}

	split(el) {
		if (el._split) return;

		el._split = new SplitType(el, { types: "lines" });
		el._lineInners = [];

		if (!el._split.lines?.length) {
			this.markVisible(el);
			return;
		}

		el._split.lines.forEach((line) => {
			line.classList.add("split-line-mask");

			const inner = document.createElement("div");
			inner.className = "split-line-inner";

			while (line.firstChild) {
				inner.appendChild(line.firstChild);
			}

			line.appendChild(inner);
			el._lineInners.push(inner);
			gsap.set(inner, { yPercent: 100 });
		});

		el.classList.add("is-split");
	}

	markVisible(el) {
		el.classList.add("is-split", "is-revealed");
		this.checkHeaderComplete(el);
	}

	reveal(el) {
		if (this.revealed.has(el)) return;
		this.revealed.add(el);

		this.observer?.unobserve(el);

		if (!el._split) this.split(el);

		const lines = el._lineInners || [];
		if (!lines.length) {
			this.markVisible(el);
			return;
		}

		gsap.killTweensOf(lines);

		const delay = parseFloat(el.dataset.splitReveal) || 0;

		gsap.to(lines, {
			yPercent: 0,
			duration: DURATION,
			ease: EASE,
			stagger: LINE_STAGGER,
			delay,
			onComplete: () => {
				// Pas de revert : le DOM modifié + is-split retiré faisait disparaître le texte.
				lines.forEach((line) => gsap.set(line, { clearProps: "transform" }));
				el.classList.add("is-revealed");
				this.checkHeaderComplete(el);
			},
		});
	}

	checkHeaderComplete(el) {
		if (!el?.closest?.(".c-header")) return;

		const items = document.querySelectorAll(".c-header [data-split-reveal]");
		if (!items.length) return;

		const allRevealed = [...items].every((item) => item.classList.contains("is-revealed"));
		if (allRevealed) {
			document.dispatchEvent(new CustomEvent("meral:header-text-revealed"));
		}
	}

	destroy() {
		this.observer?.disconnect();
		this.items?.forEach((el) => this.cleanup(el));
	}

	cleanup(el) {
		if (!el._split) return;

		el._lineInners?.forEach((inner) => gsap.killTweensOf(inner));
		el._split.revert();
		el._split = null;
		el._lineInners = null;
		el.classList.remove("is-split", "is-revealed");
	}
}
